import { Link, useNavigate } from 'react-router-dom'
import { NavUser } from '@/components/nav-user'
import { RailSettings } from '@/components/rail-settings'
import { shimmer, shimmerHost } from '@/components/shimmer'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { WarikeMark } from '@/components/warike-mark'
import { NAV_SECTIONS, type NavSection, type SectionId } from '@/lib/nav'
import { prefetchProps, prefetchRoute } from '@/lib/route-chunks'
import { cn } from '@/lib/utils'

/**
 * The outer rail: one icon per section — its name is the button's accessible
 * label and a tooltip, never printed — the brand mark on top, and at the
 * bottom the Settings button with the account avatar under it. Settings is a
 * section (it owns the inner menu on /settings/*) but not a tile in the list:
 * its one door is the gear at the foot. Selecting a section swaps the inner
 * menu beside
 * it and jumps to the section's first destination — a section with no
 * destinations yet only swaps the menu.
 *
 * The rail never collapses; the collapse button in the header folds the INNER
 * menu away, and the rail is what remains — so the app is never left without
 * a way to move between sections.
 */
export function NavRail({
  section,
  onSelect,
}: {
  section: SectionId
  onSelect: (id: SectionId) => void
}) {
  const navigate = useNavigate()

  const go = (s: NavSection) => {
    onSelect(s.id)
    const dest = destination(s)
    if (dest) navigate(dest)
  }

  return (
    <nav
      aria-label="Sections"
      className="sticky top-0 z-20 hidden h-svh w-[var(--rail-width,3.5rem)] shrink-0 flex-col items-center gap-1 border-r border-sidebar-border bg-sidebar py-3 text-sidebar-foreground md:flex"
    >
      {/* The mark alone, no letters: on hover it breathes the band's colours
          instead of the timed tint that hands light to a wordmark — the
          wordmark (and its own sweep) lives in the header now. */}
      <Link
        to="/"
        {...prefetchProps('/')}
        data-slot="rail-mark"
        {...shimmerHost}
        aria-label="Warike home"
        className="mb-2 p-1.5 text-foreground"
      >
        <WarikeMark {...shimmer({ variant: 'icon' })} className="icon-glow size-5" />
      </Link>

      {NAV_SECTIONS.filter((s) => !s.atFoot).map((s) => {
        const active = s.id === section
        return (
          <Tooltip key={s.id}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => go(s)}
                // The section's first page starts loading while the pointer is
                // still on its way to the click.
                onPointerEnter={() => prefetchSection(s)}
                onFocus={() => prefetchSection(s)}
                aria-label={s.label}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'glow-on-focus relative flex size-10 items-center justify-center rounded-lg outline-none',
                  // box-shadow and transform ride the transition so the focus halo
                  // fades and the press eases — the host owns the timing.
                  'transition-[color,background-color,box-shadow,transform] duration-[var(--motion-interaction)] ease-out',
                  'active:translate-y-px active:scale-[0.98] active:duration-[var(--motion-press)]',
                  'motion-reduce:transition-none motion-reduce:active:translate-y-0 motion-reduce:active:scale-100',
                  // The WHOLE tile is the selection — a filled square with a hairline
                  // around the icon, no edge bar. The hairline is what keeps it
                  // legible in both themes. The fill is the FOREGROUND at a low alpha,
                  // so it reads as the same weight of tile on paper and on ink.
                  active
                    ? 'bg-foreground/[0.08] text-foreground shadow-xs ring-1 ring-sidebar-border'
                    : // A HALF-strength hover, deliberately: a full-token hover
                      // would render a hovered section as the selected one.
                      'text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground',
                )}
              >
                <s.icon className="size-5" stroke={active ? 2 : 1.7} aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">{s.label}</TooltipContent>
          </Tooltip>
        )
      })}

      {/* The foot: Settings above account. mt-auto pins them to the bottom
          whatever the section count. The theme lives in the account menu. */}
      <div className="mt-auto flex flex-col items-center gap-2">
        <RailSettings onSelect={() => onSelect('settings')} />
        <NavUser />
      </div>
    </nav>
  )
}

/** Where a section's tile goes: its first page, or its first page's first child. */
function destination(s: NavSection): string | undefined {
  const first = s.items[0]
  return first ? (first.url ?? first.children?.[0]?.url) : undefined
}

function prefetchSection(s: NavSection): void {
  const dest = destination(s)
  if (dest) prefetchRoute(dest)
}
